import { all, put, takeLatest, takeEvery, call } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import { push } from "connected-react-router";
import { toast } from "react-toastify";
import { REPORTING_EMPLOYEES_SEARCH_PATH } from "@routes/constants";
import { route } from "@routes/helper";
import actionCreators from "@modules/employee/actionCreator";
import {
  UpdateEmployeeDetails,
  DeleteEmployee,
  SetEmployeesDefaultData,
  AddAlertExclusion,
  UpdateAlertExclusion,
  GetEmployeeAlertExclusions,
  RemoveAlertExclusion,
  GetClientAlert,
  GetEmployeeAlertExclusionSchema,
} from "@modules/employee/constants/actions";
import type { EmployeeFormData } from "@modules/employee/types";
import type { PaginationType } from "@types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { parseSchema } from "@utils/schema";

type ClientRequest = $Exact<{
  clientId: string,
}>;

type EmployeeRequest = $Exact<{
  ...ClientRequest,
  employeeId: string,
  page?: number,
}>;
type UpdateEmployeeRequest = $Exact<{
  ...EmployeeRequest,
  formData: EmployeeFormData,
  statusUpdate?: boolean,
}>;

type UpdateAlertExclusionRequest = $Exact<{
  ...UpdateEmployeeRequest,
  employeeAlertExclusionId: number,
}>;

type RemoveEmployeeAlertExclusion = $Exact<{
  ...EmployeeRequest,
  employeeAlertExclusionId: number,
}>;

type ClientAlertRequest = $Exact<{
  ...ClientRequest,
  alertId: number,
}>;

export function* onGetEmployeeAlertExclusions({
  payload,
}: Action<EmployeeRequest>): Generator<
  *,
  void,
  $Exact<{
    data: {
      content: any,
      pagination: PaginationType,
      totalElements: number,
    },
  }>,
> {
  try {
    if (!payload || !payload.employeeId || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/employee-alert-exclusion`,
    );
    yield put(actionCreators.GetEmployeeAlertExclusionsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetEmployeeAlertExclusionsError(e.response));
  }
}

export function* onUpdateEmployeeDetails({
  payload,
}: Action<UpdateEmployeeRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.employeeId || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.put,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}`,
      {
        ...payload.formData,
        reasonForUpdate: "NOT_DISCLOSED",
      },
    );

    yield all([
      put(
        actionCreators.GetEmployees({
          clientId: payload.clientId,
          filters: {
            id: payload.employeeId,
          },
        }),
      ),
      put(
        actionCreators.GetEmployeeChangelog({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
          page: 1,
        }),
      ),
      ,
      put(actionCreators.UpdateEmployeeDetailsSuccess(response.data)),
      put(sendSuccessMessage("EMPLOYEE_UPDATED")),
    ]);

    if (payload.statusUpdate) {
      yield put(
        actionCreators.GetUserAccount({
          clientId: payload.clientId,
          userId: payload.employeeId,
        }),
      );
    }
  } catch (e) {
    yield put(actionCreators.UpdateEmployeeDetailsError(e.response));
  }
}

export function* onAddAlertExclusion({
  payload,
}: Action<UpdateEmployeeRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.employeeId || !payload.clientId || !payload.formData) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.post,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/employee-alert-exclusion`,
      payload.formData,
    );

    yield all([
      put(sendSuccessMessage("ALERT_EXCLUSION_ADDED")),
      put(actionCreators.SetShowExcludeEmployeeFromAlertsAddModal({ visible: false })),
      put(actionCreators.ClearDataAlertExlusionForm()),
      put(
        actionCreators.GetEmployeeAlertExclusions({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.AddAlertExclusionError(e.response));
  }
}

export function* onUpdateAlertExclusion({
  payload,
}: Action<UpdateAlertExclusionRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.employeeId || !payload.clientId || !payload.formData) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.put,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/employee-alert-exclusion/${payload.employeeAlertExclusionId}`,
      payload.formData,
    );

    yield all([
      put(sendSuccessMessage("ALERT_EXCLUSION_UPDATED")),
      put(actionCreators.SetShowEditAlertExclusionModal({ visible: false })),
      put(actionCreators.ClearDataAlertExlusionForm()),
      put(
        actionCreators.GetEmployeeAlertExclusions({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateAlertExclusionError(e.response));
  }
}

export function* onRemoveAlertExclusion({
  payload,
}: Action<RemoveEmployeeAlertExclusion>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.employeeId || !payload.clientId || !payload.employeeAlertExclusionId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.delete,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}/employee-alert-exclusion/${payload.employeeAlertExclusionId}`,
    );
    yield all([
      put(actionCreators.SetShowRemoveAlertExclusionModal({ visible: false })),
      put(sendSuccessMessage("ALERT_EXCLUSION_REMOVED")),
      put(
        actionCreators.GetEmployeeAlertExclusions({
          clientId: payload.clientId,
          employeeId: payload.employeeId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.RemoveAlertExclusionError(e.response));
  }
}

export function* onGetClientAlert({
  payload,
}: Action<ClientAlertRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId || !payload.alertId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}`,
    );
    yield all([put(actionCreators.GetClientAlertSuccess(response.data))]);
  } catch (e) {
    yield put(actionCreators.GetClientAlertError(e.response));
  }
}

//TODO: Seems unused
export function* onGetEmployeeAlertExclusionSchema({
  payload,
}: Action<ClientRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const schema = yield parseSchema("EmployeeAlertExclusionDTO", { clientId: payload.clientId });

    yield all([put(actionCreators.SetEmployeeAlertExclusionSchema(schema))]);
  } catch (e) {
    yield put(actionCreators.GetEmployeeAlertExclusionSchemaError(e.response));
  }
}
export function* onDeleteEmployee({
  payload,
}: Action<EmployeeRequest>): Generator<*, void, Object> {
  try {
    if (!payload || !payload.clientId || !payload.employeeId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const response = yield call(
      http.delete,
      `/api/v1/employee-admin/client/${payload.clientId}/employee/${payload.employeeId}`,
    );
    yield all([
      put(push(route(REPORTING_EMPLOYEES_SEARCH_PATH, { clientId: payload.clientId }))),
      put(actionCreators.SetEmployeesDefaultData()),
      put(actionCreators.DeleteEmployeeSuccess()),
    ]);
    yield setTimeout(() => {
      toast.success("Employee record deleted");
    }, 10);
  } catch (e) {
    yield put(actionCreators.DeleteEmployeeError());
    toast.error(
      e?.response?.data?.messages[0] ||
        `Error while trying to delete employee ${payload.employeeId}`,
    );
  }
}

export function* PersonalDetailsSagas(): Generator<*, void, void> {
  yield all([takeEvery(GetEmployeeAlertExclusions, onGetEmployeeAlertExclusions)]);
  yield all([takeLatest(UpdateEmployeeDetails, onUpdateEmployeeDetails)]);
  yield all([takeLatest(DeleteEmployee, onDeleteEmployee)]);
  yield all([takeEvery(AddAlertExclusion, onAddAlertExclusion)]);
  yield all([takeEvery(UpdateAlertExclusion, onUpdateAlertExclusion)]);
  yield all([takeEvery(RemoveAlertExclusion, onRemoveAlertExclusion)]);
  yield all([takeEvery(GetClientAlert, onGetClientAlert)]);
  yield all([takeEvery(GetEmployeeAlertExclusionSchema, onGetEmployeeAlertExclusionSchema)]);
}

export default PersonalDetailsSagas;
